import React, { useEffect, useState } from "react";
import { Bell, Users, ShieldCheck, Building2, Settings, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const catIcon = { Team: Users, Audit: ShieldCheck, Client: Building2, System: Settings };
const catTone = {
  Team: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300",
  Audit: "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300",
  Client: "bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
  System: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
};

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationsArea() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => endpoints.listNotifications().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await endpoints.deleteNotification(id); toast.success("Notification deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  const clearAll = async () => {
    if (!window.confirm(`Delete all ${items.length} notifications?`)) return;
    try { await endpoints.clearAllNotifications(); toast.success("All notifications cleared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = filter === "all" ? items : items.filter((n) => n.category.toLowerCase() === filter);

  return (
    <div data-testid="notifications-area" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center">
          <Bell className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Notifications</h2>
          <p className="text-xs text-slate-500">Latest updates</p>
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="notifications-filter-tabs" className="h-8 bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="all" className="text-xs h-6" data-testid="notif-filter-all">All</TabsTrigger>
            <TabsTrigger value="audit" className="text-xs h-6" data-testid="notif-filter-audit">Audit</TabsTrigger>
            <TabsTrigger value="client" className="text-xs h-6" data-testid="notif-filter-client">Client</TabsTrigger>
            <TabsTrigger value="team" className="text-xs h-6" data-testid="notif-filter-team">Team</TabsTrigger>
          </TabsList>
        </Tabs>
        {items.length > 0 && (
          <button onClick={clearAll} data-testid="notifications-clear-all" className="text-[11px] text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 ml-2">
            <Trash2 className="h-3 w-3" /> Clear all
          </button>
        )}
      </div>

      <ScrollArea className="h-[300px] pr-2">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">No notifications.</div>
        ) : (
          <ul className="space-y-2.5">
            {filtered.map((n) => {
              const Icon = catIcon[n.category] || Bell;
              return (
                <li key={n.id} data-testid={`notif-item-${n.id}`} className="group flex items-start gap-3 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                  <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 ${catTone[n.category] || ""}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold truncate">{n.title}</span>
                      <Badge variant="outline" className="text-[10px] rounded-full">{n.category}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <div className="text-[10px] text-slate-400 mt-1 font-medium">{timeAgo(n.created_at)}</div>
                  </div>
                  <button onClick={() => del(n.id)} data-testid={`delete-notif-${n.id}`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition" title="Delete">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}
